/**
 * Multipurpose Content JavaScript
 * Enhanced functionality for the new multipurpose content section
 */

class MultipurposeContent {
  constructor() {
    this.sections = document.querySelectorAll('.multipurpose-content');
    this.observerOptions = {
      threshold: 0.1,
      rootMargin: '0px 0px -50px 0px'
    };
    
    this.init();
  }

  init() {
    this.setupIntersectionObserver();
    this.setupInteractions();
    this.setupAccessibility();
    this.setupAccordions();
    this.setupTables();
    this.setupResponsiveGrids();
    this.setupButtonImageTabs();
  }

  /**
   * Setup responsive grid columns
   */
  setupResponsiveGrids() {
    this.sections.forEach(section => {
      const boxContainers = section.querySelectorAll('.multipurpose-content__boxes, .multipurpose-content__icon-grid, .multipurpose-content__cards-grid, .multipurpose-content__stats, .multipurpose-content__process-steps');
      
      boxContainers.forEach(container => {
        const desktopColumns = section.dataset.boxesPerRowDesktop || '3';
        const mobileColumns = section.dataset.boxesPerRowMobile || '1';
        
        container.style.setProperty('--desktop-columns', desktopColumns);
        container.style.setProperty('--mobile-columns', mobileColumns);
      });

      const galleryContainers = section.querySelectorAll('.multipurpose-content__gallery');
      
      galleryContainers.forEach(container => {
        const desktopImages = section.dataset.imagesPerRowDesktop || '4';
        const mobileImages = section.dataset.imagesPerRowMobile || '2';
        
        container.style.setProperty('--desktop-columns', desktopImages);
        container.style.setProperty('--mobile-columns', mobileImages);
      });
    });
  }

  /**
   * Setup Intersection Observer for scroll animations
   */
  setupIntersectionObserver() {
    if (!('IntersectionObserver' in window)) {
      // Fallback for browsers without IntersectionObserver
      this.sections.forEach(section => {
        if (section.dataset.scrollAnimation === 'true') {
          section.classList.add('animate-in');
        }
      });
      return;
    }

    const observer = new IntersectionObserver((entries) => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          const section = entry.target;
          if (section.dataset.scrollAnimation === 'true') {
            section.classList.add('animate-in');
            
            // Animate child elements with stagger
            this.animateChildElements(section);
          }
        }
      });
    }, this.observerOptions);

    this.sections.forEach(section => {
      observer.observe(section);
    });
  }

  /**
   * Animate child elements with stagger effect
   */
  animateChildElements(section) {
    const boxes = section.querySelectorAll('.multipurpose-content__box');
    const images = section.querySelectorAll('.multipurpose-content__image-box');
    const timelineItems = section.querySelectorAll('.multipurpose-content__timeline-item');
    const accordionItems = section.querySelectorAll('.multipurpose-content__accordion-item');
    const iconFeatures = section.querySelectorAll('.multipurpose-content__icon-feature-box');
    const animatableElements = section.querySelectorAll('[data-animation]');
    const statsCounters = section.querySelectorAll('[data-count]');
    
    const elements = [...boxes, ...images, ...timelineItems, ...accordionItems, ...iconFeatures, ...animatableElements];

    elements.forEach((element, index) => {
      // Get custom animation speed and delay
      const animationSpeed = element.style.getPropertyValue('--animation-speed') || '600ms';
      const animationDelay = element.style.getPropertyValue('--animation-delay') || '0ms';
      const baseDelay = parseInt(animationDelay) || 0;
      const staggerDelay = index * 100; // Stagger animation by 100ms
      
      // Apply custom timing
      if (animationSpeed !== '600ms') {
        element.style.transitionDuration = animationSpeed;
      }
      
      setTimeout(() => {
        element.classList.add('animate-in');
        element.style.opacity = '1';
        element.style.transform = 'translateY(0) translateX(0) scale(1) rotate(0deg) rotateX(0deg)';
      }, baseDelay + staggerDelay);
    });

    // Animate stats counters
    this.animateStatsCounters(statsCounters);
  }

  /**
   * Animate stats counters with counting effect
   */
  animateStatsCounters(counters) {
    counters.forEach((counter, index) => {
      const target = parseInt(counter.getAttribute('data-count')) || 0;
      const duration = 2000; // 2 seconds
      const increment = target / (duration / 16); // 60fps
      let current = 0;
      
      setTimeout(() => {
        const updateCounter = () => {
          current += increment;
          if (current < target) {
            counter.textContent = Math.floor(current);
            requestAnimationFrame(updateCounter);
          } else {
            counter.textContent = target;
          }
        };
        updateCounter();
      }, index * 200); // Stagger counter animations
    });
  }

  /**
   * Setup interactive behaviors
   */
  setupInteractions() {
    this.setupBoxInteractions();
    this.setupImageInteractions();
    this.setupHoverEffects();
    this.setupClickEffects();
  }

  /**
   * Setup box interactions and hover effects
   */
  setupBoxInteractions() {
    const boxes = document.querySelectorAll('.multipurpose-content__box, .multipurpose-content__icon-feature-box');
    
    boxes.forEach(box => {
      // Initialize opacity for animation
      this.initializeElementAnimation(box);

      // Enhanced hover effects
      box.addEventListener('mouseenter', (e) => {
        this.handleBoxHover(e.target, true);
      });

      box.addEventListener('mouseleave', (e) => {
        this.handleBoxHover(e.target, false);
      });

      // Click interactions
      box.addEventListener('click', (e) => {
        if (!e.target.closest('a')) {
          this.handleBoxClick(e.target);
        }
      });
    });
  }

  /**
   * Setup image interactions
   */
  setupImageInteractions() {
    const imageBoxes = document.querySelectorAll('.multipurpose-content__image-box');
    
    imageBoxes.forEach(imageBox => {
      // Initialize for animation
      this.initializeElementAnimation(imageBox);

      // Lazy loading enhancement
      const img = imageBox.querySelector('img');
      if (img) {
        this.setupLazyLoading(img);
      }

      // Parallax effect on scroll (subtle)
      if (!window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
        this.setupParallaxEffect(imageBox);
      }
    });
  }

  /**
   * Initialize element for animation
   */
  initializeElementAnimation(element) {
    const animation = element.dataset.animation;
    if (animation && animation !== 'none') {
      element.style.opacity = '0';
      switch (animation) {
        case 'fade-up':
          element.style.transform = 'translateY(30px)';
          break;
        case 'slide-left':
          element.style.transform = 'translateX(-30px)';
          break;
        case 'slide-right':
          element.style.transform = 'translateX(30px)';
          break;
        case 'zoom-in':
          element.style.transform = 'scale(0.8)';
          break;
      }
      element.style.transition = 'all 0.6s ease';
    }
  }

  /**
   * Setup enhanced hover effects
   */
  setupHoverEffects() {
    // Enhanced glow effect for boxes
    const glowBoxes = document.querySelectorAll('.multipurpose-content__box[data-hover-effect="glow"]');
    glowBoxes.forEach(box => {
      box.addEventListener('mouseenter', () => {
        box.style.boxShadow = '0 0 30px rgba(59, 130, 246, 0.6)';
      });
      
      box.addEventListener('mouseleave', () => {
        const originalShadow = this.getOriginalBoxShadow(box);
        box.style.boxShadow = originalShadow;
      });
    });

    // Enhanced image hover effects
    const zoomImages = document.querySelectorAll('.multipurpose-content__image-box[data-hover-effect="zoom"]');
    zoomImages.forEach(imageBox => {
      const img = imageBox.querySelector('.multipurpose-content__image');
      if (img) {
        imageBox.addEventListener('mouseenter', () => {
          img.style.transform = 'scale(1.1)';
        });
        
        imageBox.addEventListener('mouseleave', () => {
          img.style.transform = 'scale(1)';
        });
      }
    });
  }

  /**
   * Setup click effects
   */
  setupClickEffects() {
    const clickableElements = document.querySelectorAll('.multipurpose-content__box, .multipurpose-content__image-box, .multipurpose-content__icon-feature-box');
    
    clickableElements.forEach(element => {
      element.addEventListener('click', (e) => {
        if (!e.target.closest('a, button')) {
          this.createRippleEffect(e, element);
        }
      });
    });
  }

  /**
   * Create ripple effect on click
   */
  createRippleEffect(event, element) {
    const ripple = document.createElement('div');
    const rect = element.getBoundingClientRect();
    const size = Math.max(rect.width, rect.height);
    const x = event.clientX - rect.left - size / 2;
    const y = event.clientY - rect.top - size / 2;

    ripple.style.cssText = `
      position: absolute;
      border-radius: 50%;
      background: rgba(255, 255, 255, 0.6);
      pointer-events: none;
      width: ${size}px;
      height: ${size}px;
      left: ${x}px;
      top: ${y}px;
      animation: ripple-animation 0.6s ease-out;
      transform: scale(0);
      z-index: 1000;
    `;

    // Ensure element has relative positioning
    const originalPosition = getComputedStyle(element).position;
    if (originalPosition === 'static') {
      element.style.position = 'relative';
    }

    element.appendChild(ripple);

    // Add ripple animation if not already added
    if (!document.querySelector('#ripple-styles')) {
      const style = document.createElement('style');
      style.id = 'ripple-styles';
      style.textContent = `
        @keyframes ripple-animation {
          to {
            transform: scale(4);
            opacity: 0;
          }
        }
      `;
      document.head.appendChild(style);
    }

    setTimeout(() => {
      ripple.remove();
      if (originalPosition === 'static') {
        element.style.position = '';
      }
    }, 600);
  }

  /**
   * Handle box hover effects
   */
  handleBoxHover(box, isHover) {
    const hoverEffect = box.dataset.hoverEffect;
    
    if (hoverEffect === 'bounce' && isHover) {
      box.style.animation = 'bounce 0.6s ease';
      setTimeout(() => {
        box.style.animation = '';
      }, 600);
    }
  }

  /**
   * Handle box click interactions
   */
  handleBoxClick(box) {
    // Add subtle scale effect on click
    box.style.transform = 'scale(0.98)';
    setTimeout(() => {
      box.style.transform = '';
    }, 150);
  }

  /**
   * Setup lazy loading for images
   */
  setupLazyLoading(img) {
    if ('loading' in HTMLImageElement.prototype) {
      // Native lazy loading supported
      img.loading = 'lazy';
    } else {
      // Fallback for browsers without native lazy loading
      const imageObserver = new IntersectionObserver((entries) => {
        entries.forEach(entry => {
          if (entry.isIntersecting) {
            const image = entry.target;
            if (image.dataset.src) {
              image.src = image.dataset.src;
              image.removeAttribute('data-src');
            }
            imageObserver.unobserve(image);
          }
        });
      });

      imageObserver.observe(img);
    }
  }

  /**
   * Setup subtle parallax effect
   */
  setupParallaxEffect(element) {
    const handleScroll = () => {
      const rect = element.getBoundingClientRect();
      const speed = 0.3;
      const yPos = -(rect.top * speed);
      
      if (rect.bottom >= 0 && rect.top <= window.innerHeight) {
        element.style.transform = `translateY(${yPos}px)`;
      }
    };

    // Throttle scroll events
    let ticking = false;
    const throttledScroll = () => {
      if (!ticking) {
        requestAnimationFrame(() => {
          handleScroll();
          ticking = false;
        });
        ticking = true;
      }
    };

    window.addEventListener('scroll', throttledScroll, { passive: true });
  }

  /**
   * Setup accordion functionality
   */
  setupAccordions() {
    const accordionHeaders = document.querySelectorAll('.multipurpose-content__accordion-header');
    
    accordionHeaders.forEach(header => {
      header.addEventListener('click', () => {
        this.toggleAccordion(header);
      });

      header.addEventListener('keydown', (e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          this.toggleAccordion(header);
        }
      });
    });
  }

  /**
   * Toggle accordion item
   */
  toggleAccordion(header) {
    const content = header.nextElementSibling;
    const icon = header.querySelector('.multipurpose-content__accordion-icon');
    const isExpanded = header.getAttribute('aria-expanded') === 'true';

    // Close other accordions in the same group (optional)
    const accordionGroup = header.closest('.multipurpose-content__accordion');
    const otherHeaders = accordionGroup.querySelectorAll('.multipurpose-content__accordion-header');
    
    otherHeaders.forEach(otherHeader => {
      if (otherHeader !== header) {
        otherHeader.setAttribute('aria-expanded', 'false');
        const otherContent = otherHeader.nextElementSibling;
        const otherIcon = otherHeader.querySelector('.multipurpose-content__accordion-icon');
        
        otherContent.style.display = 'none';
        otherContent.classList.remove('open');
        if (otherIcon) otherIcon.textContent = '+';
      }
    });

    // Toggle current accordion
    if (isExpanded) {
      header.setAttribute('aria-expanded', 'false');
      content.style.display = 'none';
      content.classList.remove('open');
      if (icon) icon.textContent = '+';
    } else {
      header.setAttribute('aria-expanded', 'true');
      content.style.display = 'block';
      content.classList.add('open');
      if (icon) icon.textContent = '−';
    }
  }

  /**
   * Setup table interactions
   */
  setupTables() {
    const tables = document.querySelectorAll('.multipurpose-content__table-content table');
    
    tables.forEach(table => {
      // Add sortable functionality to tables
      this.makeSortable(table);
      
      // Add responsive behavior
      this.makeTableResponsive(table);
    });
  }

  /**
   * Make table sortable
   */
  makeSortable(table) {
    const headers = table.querySelectorAll('th');
    
    headers.forEach((header, index) => {
      header.style.cursor = 'pointer';
      header.style.userSelect = 'none';
      header.setAttribute('role', 'button');
      header.setAttribute('tabindex', '0');
      
      // Add sort indicator
      const sortIcon = document.createElement('span');
      sortIcon.innerHTML = ' ↕️';
      sortIcon.style.fontSize = '0.8em';
      header.appendChild(sortIcon);

      header.addEventListener('click', () => {
        this.sortTable(table, index);
      });

      header.addEventListener('keydown', (e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          this.sortTable(table, index);
        }
      });
    });
  }

  /**
   * Sort table by column
   */
  sortTable(table, columnIndex) {
    const tbody = table.querySelector('tbody');
    const rows = Array.from(tbody.querySelectorAll('tr'));
    const header = table.querySelectorAll('th')[columnIndex];
    
    // Determine sort direction
    const isAscending = !header.classList.contains('sort-desc');
    
    // Reset all headers
    table.querySelectorAll('th').forEach(th => {
      th.classList.remove('sort-asc', 'sort-desc');
      const icon = th.querySelector('span');
      if (icon) icon.innerHTML = ' ↕️';
    });

    // Sort rows
    rows.sort((a, b) => {
      const aVal = a.cells[columnIndex].textContent.trim();
      const bVal = b.cells[columnIndex].textContent.trim();
      
      // Try to parse as numbers
      const aNum = parseFloat(aVal);
      const bNum = parseFloat(bVal);
      
      if (!isNaN(aNum) && !isNaN(bNum)) {
        return isAscending ? aNum - bNum : bNum - aNum;
      }
      
      // String comparison
      return isAscending ? 
        aVal.localeCompare(bVal) : 
        bVal.localeCompare(aVal);
    });

    // Update header styling
    header.classList.add(isAscending ? 'sort-asc' : 'sort-desc');
    const icon = header.querySelector('span');
    if (icon) {
      icon.innerHTML = isAscending ? ' ↑' : ' ↓';
    }

    // Reorder DOM with animation
    rows.forEach((row, index) => {
      setTimeout(() => {
        tbody.appendChild(row);
      }, index * 10);
    });
  }

  /**
   * Make table responsive
   */
  makeTableResponsive(table) {
    const wrapper = table.closest('.multipurpose-content__table-wrapper');
    
    // Add scroll hint for mobile
    if (window.innerWidth <= 768) {
      const scrollHint = document.createElement('div');
      scrollHint.textContent = '← Scroll to see more →';
      scrollHint.style.cssText = `
        text-align: center;
        font-size: 0.8rem;
        color: #666;
        padding: 0.5rem;
        background: #f8f9fa;
        border-top: 1px solid #e5e5e5;
      `;
      wrapper.appendChild(scrollHint);
    }
  }

  /**
   * Setup accessibility features
   */
  setupAccessibility() {
    // Add ARIA labels
    this.sections.forEach(section => {
      const heading = section.querySelector('.multipurpose-content__heading');
      if (heading) {
        section.setAttribute('aria-labelledby', heading.id || 'multipurpose-content-heading');
        if (!heading.id) {
          heading.id = 'multipurpose-content-heading-' + Math.random().toString(36).substr(2, 9);
        }
      }
    });

    // Add focus management
    this.setupFocusManagement();
    this.setupKeyboardNavigation();
  }

  /**
   * Setup focus management for keyboard navigation
   */
  setupFocusManagement() {
    const focusableElements = document.querySelectorAll(
      '.multipurpose-content__box, .multipurpose-content__image-box, .multipurpose-content__accordion-header, .multipurpose-content__icon-feature-box'
    );

    focusableElements.forEach(element => {
      if (!element.hasAttribute('tabindex')) {
        element.setAttribute('tabindex', '0');
      }

      element.addEventListener('focus', () => {
        element.style.outline = '2px solid #007bff';
        element.style.outlineOffset = '2px';
      });

      element.addEventListener('blur', () => {
        element.style.outline = '';
        element.style.outlineOffset = '';
      });
    });
  }

  /**
   * Setup keyboard navigation
   */
  setupKeyboardNavigation() {
    const interactiveElements = document.querySelectorAll(
      '.multipurpose-content__box, .multipurpose-content__image-box, .multipurpose-content__icon-feature-box'
    );

    interactiveElements.forEach(element => {
      element.addEventListener('keydown', (e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          
          // Check if element has a link
          const link = element.querySelector('a');
          if (link) {
            link.click();
          } else {
            // Trigger click effect
            this.createRippleEffect(e, element);
          }
        }
      });
    });
  }

  /**
   * Get original box shadow value
   */
  getOriginalBoxShadow(box) {
    const computedStyle = window.getComputedStyle(box);
    const originalShadow = computedStyle.boxShadow;
    return originalShadow !== 'none' ? originalShadow : '0 2px 8px rgba(0,0,0,0.1)';
  }

  /**
   * Utility method to debounce functions
   */
  debounce(func, wait) {
    let timeout;
    return function executedFunction(...args) {
      const later = () => {
        clearTimeout(timeout);
        func(...args);
      };
      clearTimeout(timeout);
      timeout = setTimeout(later, wait);
    };
  }

  /**
   * Setup responsive font loading
   */
  setupFontLoading() {
    // Load Google Fonts for custom font families
    const fontFamilies = document.querySelectorAll('.font-quicksand, .font-comfortaa');
    
    if (fontFamilies.length > 0) {
      const link = document.createElement('link');
      link.href = 'https://fonts.googleapis.com/css2?family=Quicksand:wght@300;400;500;600;700&family=Comfortaa:wght@300;400;500;600;700&display=swap';
      link.rel = 'stylesheet';
      document.head.appendChild(link);
    }
  }

  /**
   * Handle window resize
   */
  handleResize() {
    // Recalculate layouts on resize
    const debouncedResize = this.debounce(() => {
      // Refresh parallax calculations
      const parallaxElements = document.querySelectorAll('.multipurpose-content__image-box');
      parallaxElements.forEach(element => {
        element.style.transform = '';
      });
      
      // Refresh table responsive features
      const tables = document.querySelectorAll('.multipurpose-content__table-content table');
      tables.forEach(table => {
        this.makeTableResponsive(table);
      });
    }, 250);

    window.addEventListener('resize', debouncedResize);
  }

  /**
   * Setup Button+Image tab functionality
   */
  setupButtonImageTabs() {
    const buttonImageSections = document.querySelectorAll('.multipurpose-content__button-image-wrapper');
    
    buttonImageSections.forEach(section => {
      const buttons = section.querySelectorAll('.multipurpose-content__tab-button');
      const images = section.querySelectorAll('.multipurpose-content__tab-image');
      const autoRotate = section.dataset.autoRotate === 'true';
      const rotationInterval = parseInt(section.dataset.rotationInterval) || 5000;
      
      let currentIndex = 0;
      let rotationTimer = null;

      // Setup button click handlers
      buttons.forEach((button, index) => {
        button.addEventListener('click', () => {
          this.switchToTab(index, buttons, images);
          currentIndex = index;
          
          // Reset auto-rotation timer
          if (autoRotate) {
            this.resetAutoRotation(section, buttons, images);
          }
        });

        // Keyboard navigation
        button.addEventListener('keydown', (e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            button.click();
          }
        });
      });

      // Setup auto-rotation if enabled
      if (autoRotate && buttons.length > 1) {
        this.startAutoRotation(section, buttons, images);
        
        // Pause on hover
        section.addEventListener('mouseenter', () => {
          this.pauseAutoRotation(section);
        });
        
        section.addEventListener('mouseleave', () => {
          this.startAutoRotation(section, buttons, images);
        });
      }
    });
  }

  /**
   * Switch to specific tab
   */
  switchToTab(index, buttons, images) {
    // Update buttons
    buttons.forEach((btn, i) => {
      btn.classList.toggle('active', i === index);
      btn.setAttribute('aria-selected', i === index);
    });

    // Update images with fade effect
    images.forEach((img, i) => {
      if (i === index) {
        img.style.display = 'block';
        img.classList.add('active');
        // Fade in
        setTimeout(() => {
          img.style.opacity = '1';
        }, 10);
      } else {
        img.style.opacity = '0';
        img.classList.remove('active');
        // Hide after fade out
        setTimeout(() => {
          if (!img.classList.contains('active')) {
            img.style.display = 'none';
          }
        }, 300);
      }
    });
  }

  /**
   * Start auto-rotation
   */
  startAutoRotation(section, buttons, images) {
    const rotationInterval = parseInt(section.dataset.rotationInterval) || 5000;
    let currentIndex = 0;

    section.rotationTimer = setInterval(() => {
      currentIndex = (currentIndex + 1) % buttons.length;
      this.switchToTab(currentIndex, buttons, images);
    }, rotationInterval);
  }

  /**
   * Pause auto-rotation
   */
  pauseAutoRotation(section) {
    if (section.rotationTimer) {
      clearInterval(section.rotationTimer);
      section.rotationTimer = null;
    }
  }

  /**
   * Reset auto-rotation timer
   */
  resetAutoRotation(section, buttons, images) {
    this.pauseAutoRotation(section);
    this.startAutoRotation(section, buttons, images);
  }
}

// Initialize when DOM is ready
document.addEventListener('DOMContentLoaded', () => {
  const multipurposeContent = new MultipurposeContent();
  multipurposeContent.setupFontLoading();
  multipurposeContent.handleResize();
});

// Reinitialize on theme editor changes (Shopify)
if (window.Shopify && window.Shopify.designMode) {
  document.addEventListener('shopify:section:load', () => {
    new MultipurposeContent();
  });
}